/**
 * CDF (Combined Dual Fuel) Item Transformation Utilities
 *
 * Transforms API responses for CDF templates to duplicate items per fuel type
 */

import { TextField } from 'types/apis/cxEditor';
import {
  shouldDuplicateForFuels,
  createFuelSpecificId,
  createFuelSpecificDisplayName,
  CdfItemMetadata
} from './cdf-detector';

/**
 * Transform text fields from API response for CDF templates
 * Items with attributes array containing "CDF_EL_GAS_WATER" are duplicated for each fuel type
 *
 * @param items - Original items from API (with fuelSpecificValues field)
 * @param isCdf - Whether this is a CDF template
 * @param supportedFuelTypes - Array of fuel types supported by project (e.g., ['ELECTRIC', 'GAS'])
 * @returns Transformed items array with duplicates for multi-fuel items
 *
 * @example
 * const original = [{
 *   id: 'com.bidgely.config.key',
 *   label: 'Total Spent',
 *   value: 'Total Spent on Energy',
 *   attributes: ['CDF_EL_GAS_WATER', 'OTHER_TAG'],
 *   fuelSpecificValues: {
 *     ELECTRIC: 'Total Spent on Electricity',
 *     GAS: 'Total Spent on Gas'
 *   }
 * }];
 *
 * const transformed = transformCdfTextFields(original, true, ['ELECTRIC', 'GAS']);
 * // Returns:
 * // [
 * //   {
 * //     id: 'com.bidgely.config.key__ELECTRIC',
 * //     label: 'Total Spent - ELECTRIC',
 * //     value: 'Total Spent on Electricity',
 * //     fieldName: 'com.bidgely.config.key__ELECTRIC',
 * //     metadata: { originalId: 'com.bidgely.config.key', fuelType: 'ELECTRIC', attributes: [...] }
 * //   },
 * //   {
 * //     id: 'com.bidgely.config.key__GAS',
 * //     label: 'Total Spent - GAS',
 * //     value: 'Total Spent on Gas',
 * //     fieldName: 'com.bidgely.config.key__GAS',
 * //     metadata: { originalId: 'com.bidgely.config.key', fuelType: 'GAS', attributes: [...] }
 * //   }
 * // ]
 */
export function transformCdfTextFields(
  items: Array<TextField & { attributes?: string[]; fuelSpecificValues?: Record<string, string> }>,
  isCdf: boolean,
  supportedFuelTypes: string[]
): TextField[] {
  // If not CDF or no fuel types, return items as-is
  if (!isCdf || supportedFuelTypes.length === 0) {
    return items;
  }

  const transformed: TextField[] = [];

  items.forEach((item) => {
    const hasMultiFuelTag = shouldDuplicateForFuels(item.attributes);

    if (hasMultiFuelTag) {
      // Only duplicate for fuel types that have values from the backend
      // This prevents creating DUAL entries when backend only returns ELECTRIC, GAS, WATER
      const fuelTypesToDisplay = item.fuelSpecificValues
        ? Object.keys(item.fuelSpecificValues)
        : supportedFuelTypes;

      fuelTypesToDisplay.forEach((fuelType) => {
        const metadata: CdfItemMetadata = {
          originalId: item.id,
          fuelType: fuelType,
          attributes: item.attributes
        };

        // Use fuel-specific value from API if available, otherwise fall back to base value
        const fuelSpecificValue = item.fuelSpecificValues?.[fuelType] || item.value;

        transformed.push({
          ...item,
          id: createFuelSpecificId(item.id, fuelType),
          label: createFuelSpecificDisplayName(item.label, fuelType),
          value: fuelSpecificValue, // Use fuel-specific value
          fieldName: createFuelSpecificId(item.fieldName || item.id, fuelType),
          metadata: metadata
        });
      });
    } else {
      // Not a multi-fuel item, keep as-is
      transformed.push(item);
    }
  });

  return transformed;
}

/**
 * Item from API with potential attributes field
 */
interface ApiItemWithTag {
  id: string;
  label: string;
  value?: any;
  fieldName?: string;
  description?: string;
  attributes?: string[];
  fuelSpecificValues?: Record<string, string>;
  [key: string]: any;
}

/**
 * Transform both configs and string resources from API response
 *
 * @param configsText - Configs from API
 * @param stringResourcesText - String resources from API
 * @param isCdf - Whether this is a CDF template
 * @param supportedFuelTypes - Fuel types supported by project
 * @returns Combined and transformed text fields
 */
export function transformInspectorTextFields(
  configsText: ApiItemWithTag[],
  stringResourcesText: ApiItemWithTag[],
  isCdf: boolean,
  supportedFuelTypes: string[]
): TextField[] {
  // Transform configs
  const transformedConfigs = transformCdfTextFields(
    configsText as TextField[],
    isCdf,
    supportedFuelTypes
  );

  // Transform string resources
  const transformedStrings = transformCdfTextFields(
    stringResourcesText as TextField[],
    isCdf,
    supportedFuelTypes
  );

  // Combine configs first, then string resources (maintains API order)
  return [...transformedConfigs, ...transformedStrings];
}

/**
 * Prepare save payload for CDF items
 * Extracts original ID and fuel type from metadata
 *
 * @param item - TextField with potential CDF metadata
 * @param defaultFuelType - Fallback fuel type if not in metadata
 * @returns Object with originalId and targetFuelType
 */
export function prepareCdfSaveData(
  item: TextField,
  defaultFuelType: string
): {
  originalId: string;
  targetFuelType: string;
} {
  const metadata = item.metadata as CdfItemMetadata | undefined;

  if (metadata?.originalId && metadata?.fuelType) {
    // This is a CDF duplicated item
    return {
      originalId: metadata.originalId,
      targetFuelType: metadata.fuelType
    };
  }

  // Regular item (not CDF or not duplicated)
  return {
    originalId: item.fieldName || item.id,
    targetFuelType: defaultFuelType
  };
}
